function generatedMutationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `taxonomy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const LANGUAGE_SCOPE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const languageScope = (value = 'default') => {
  const scope = String(value || 'default');
  if (!LANGUAGE_SCOPE_PATTERN.test(scope)) throw new Error('Invalid taxonomy language scope');
  return scope;
};

export function createLegacyTaxonomyApi({ client, mutationIdFactory = generatedMutationId } = {}) {
  let webserviceClient = client || null;
  const revisions = new Map();
  const pendingMutations = new Map();
  const scopeKey = (shopId, language) => `${String(shopId)}\0${language}`;

  function getClient() {
    if (!webserviceClient) {
      // eslint-disable-next-line global-require
      webserviceClient = require('util/appUtils').wsClient;
    }
    return webserviceClient;
  }

  return {
    async load(shopId, languageId = 'default') {
      const language = languageScope(languageId);
      const key = scopeKey(shopId, language);
      const response = await getClient().prepareRequest(
        'GET',
        `/system/shop/${encodeURIComponent(shopId)}/customization/taxonomy`,
        { qs: { language_id: language } },
      );
      const previousRevision = revisions.get(key) || 0;
      const loadedRevision = Number(response?.revision || 0);
      // Canonical revisions are monotonic. A slower, older GET must not lower
      // the CAS baseline used by a later publication.
      revisions.set(key, Math.max(previousRevision, loadedRevision));
      // A revision advance proves that a timed-out mutation may have committed.
      // An unchanged reload keeps its idempotency key available for retry.
      if (loadedRevision > previousRevision) pendingMutations.delete(key);
      return response;
    },

    async loadDictionary(shopId, type, languageId = 'default') {
      const language = languageScope(languageId);
      return getClient().prepareRequest(
        'GET',
        `/system/shop/${encodeURIComponent(shopId)}/customization/taxonomy/dictionary`,
        { qs: { type, language_id: language } },
      );
    },

    async save(shopId, taxonomies, config, languageId = 'default') {
      const language = languageScope(languageId);
      const key = scopeKey(shopId, language);
      const signature = JSON.stringify({ taxonomies, config });
      let pendingMutation = pendingMutations.get(key);
      if (!pendingMutation || pendingMutation.signature !== signature) {
        pendingMutation = { signature, id: mutationIdFactory() };
        pendingMutations.set(key, pendingMutation);
      }
      const response = await getClient().prepareRequest(
        'POST',
        `/system/shop/${encodeURIComponent(shopId)}/customization/taxonomy`,
        {
          body: {
            taxonomies,
            config,
            expected_revision: revisions.get(key) || 0,
            client_mutation_id: pendingMutation.id,
            language_id: language,
          },
        },
      );
      revisions.set(key, Math.max(
        revisions.get(key) || 0,
        Number(response?.revision || 0),
      ));
      pendingMutations.delete(key);
      return response;
    },
  };
}
